/*
 * Authors: mariushirjanu
 * Created: 05/02/2019
 * Upgraded: 03/10/2022
 */
import * as _ from 'underscore';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IUserAuditData, UserProfileDataService } from '../userprofile-data.service';
import { ITrainingData, IUserInfo, UserProfileService } from '../user-profile.service';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { IRoleContact } from '../../webshop/info/webshop-info-data.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { STATE_BPPROFILES_ACSLINFO, STATE_REPORT } from '@app/shared/app-states';
import { IObject } from '@type_definitions/datatypes';
import { UntypedFormGroup } from '@angular/forms';
import { EVENT_USER_PROFILE_UPDATED } from '@app/shared/app-events';
import { RoleContactType } from '@app/shared/contact/role-contact-type';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

interface IUserAuditModel {
  audit?:string[];
  auditHistoryLinkParams?:IObject;
  training?:ITrainingData;
  odmRoleContact?: IRoleContact;
  gbdmRoleContact?: IRoleContact;
  bpAcslInfoLinkParams?:IObject;
}

@Component({
  selector: 'user-audit',
  template: `
    <div class="audit">
      <h2 *ngIf="loading">
        <activity-indicator toLoad="APP.USER_PROFILE.TABS.AUDIT"></activity-indicator>
      </h2>

      <div *ngIf="!loading" class="row block-max-width">

        <!-- Audit section -->
        <div class="col-md-12" *ngIf="displayAuditView">
          <h4 class="form-title">Audit</h4>

          <div class="row">
            <div class="col-md-6">
              <div *ngFor="let audit of model.audit; trackById">
                <div class="audit-line" *ngIf="audit" [innerHTML]="audit"></div>
              </div>
              <div class="audit-line"><a uiSref="${STATE_REPORT}" [uiParams]="model.auditHistoryLinkParams" target="_blank">List of all modifications</a></div>
            </div>

            <div class="col-md-6">
              <div class="audit-line" *ngIf="model.odmRoleContact || model.gbdmRoleContact"><strong>ACSL contacts</strong>:</div>
              <div class="audit-line" *ngIf="model.odmRoleContact">PSS
                <contact-info [roleContact]="model.odmRoleContact"></contact-info>
              </div>
              <div class="audit-line" *ngIf="model.gbdmRoleContact">GBDM
                <contact-info [roleContact]="model.gbdmRoleContact"></contact-info>
              </div>
              <div class="audit-line" *ngIf="model.bpAcslInfoLinkParams"><a uiSref="${STATE_BPPROFILES_ACSLINFO}" [uiParams]="model.bpAcslInfoLinkParams" target="_blank">ACSL Info</a></div>
            </div>
          </div>
        </div>

        <!-- Training -->
        <div class="col-md-12">
          <formly [form]="form" [fields]="fields" [model]="model.training" class="form-horizontal" [noValidate]="true"></formly>
        </div>

      </div>

    </div>
  `
})
export class UserAuditComponent implements OnInit, OnDestroy {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IUserAuditModel = {};
  private userInfo:IUserInfo;
  public displayAuditView:boolean;
  private trainingStatusField:IExtraFieldConfigurationObject;
  private userProfile:IUserData;
  private eventHandler:Subscription;
  public loading:boolean;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private userProfileService:UserProfileService,
    private userProfileDataService:UserProfileDataService,
    private gaEventService:GaEventService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.userProfileService.getUserProfilePromise()
      .then(this.initController);

    this.listen();
  }

  private listen():void {
    this.eventHandler = this.gaEventService.events?.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_USER_PROFILE_UPDATED) {
        this.initController(event.data);
      }
    });
  }

  private initController = (userData:IUserData) => {
    if (!(this.authService.checkAcslUser() && this.userProfileService.getUserProfileId() && this.userProfileService.manageExternalUser())) {
      return;
    }
    this.userProfile = userData;

    this.fields = this.createFields();
    this.userInfo = this.userProfileService.getUserInfo();
    this.displayAuditView = !!userData.id;
    this.fillFields();
  };

  private fillFields = ():void => {
    // Audit: set link
    if (this.displayAuditView) {
      this.model.audit = [];
      this.model.auditHistoryLinkParams = {[ReportParameterCode.REPORT_ID]: 259, [ReportParameterShortCode.AUTO_LOAD]: true, [ReportParameterShortCode.FILTERS]: `Wvconid\t${this.userProfile.id}`};
      this.model.bpAcslInfoLinkParams = this.userProfileService.isPartnerUser() ? {profileListType: 'bpprofiles', profileId: `cc:${this.userProfile.location?.companyCountry?.id}`} : undefined;
    }

    // Training: fill in fields
    this.formlyService.fillField(this.trainingStatusField, this.userProfileDataService.getTrainingStatuses());
    this.model.training = {
      lastTrainingDate: this.userInfo.lastTrainingDate,
      trainingStatus: this.userInfo.trainingStatus,
      trainingComments: this.userInfo.trainingComments,
      responsive: this.userInfo.responsive,
      usingGA: this.userInfo.usingGA,
      canDemoGA: this.userInfo.canDemoGA
    };

    // load extra data: user profile modification (date and user) and training modification (date and user)
    this.loading = true;
    this.userProfileDataService.getAuditData(this.userInfo?.userId)
      .then((auditData:IUserAuditData) => {
        // Audit: creation and modification (date & user)
        if (this.displayAuditView) {
          this.model.audit.push(
            this.formattingService.formatAudit(undefined, auditData.registrationDate, 'Registered'),
            this.formattingService.formatAudit(auditData.creationUser, auditData.creationDate, 'Created'),
            this.formattingService.formatAudit(auditData.modificationUser, auditData.modificationDate, 'Last modification'),
            this.formattingService.formatAudit(undefined, auditData.firstLoginDate, 'First login'),
            this.formattingService.formatAudit(undefined, auditData.lastLoginDate, 'Last login'),
            auditData.timeWithACSL? `Time with ACSL <strong>${auditData.timeWithACSL}</strong>` : undefined
          );

          if(auditData.roleContacts) {
            this.model.odmRoleContact = _.find(auditData.roleContacts, { contactType: RoleContactType.PSS });
            this.model.gbdmRoleContact = _.find(auditData.roleContacts, { contactType: RoleContactType.GBDM });
          }
        }

        // Training: modification (date & user)
        this.model.training.trainingModification = this.userProfile.userTraining && this.formattingService.formatAudit(auditData.trainingModificationUser, auditData.trainingModificationDate, 'Modified');

        this.model.audit = _.compact(this.model.audit);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-md-2 col-sm-3';
    const valueClass:string = 'col-md-5 col-sm-7';
    const checkboxClass:string = 'col-sm-4';

    const fields:IExtraFieldConfigurationObject[] = [];

    // training
    fields.push(
      this.formlyService.aTitle('Training')
        .build()
    );

    fields.push(
      this.formlyService.aDateInput('lastTrainingDate', 'APP.AUDIT.LASTTRAININGDATE.LABEL')
        .withFormat(this.formattingService.formatDate)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.updateUserTrainingData)
        .build()
    );

    fields.push(
      this.trainingStatusField = this.formlyService.aSelect('trainingStatus', 'APP.AUDIT.TRAININGSTATUS.LABEL')
        .withFormat(this.formattingService.formatLov)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.updateUserTrainingData)
        .build()
    );

    fields.push(
      this.formlyService.aTextarea('trainingComments', 'APP.AUDIT.TRAININGCOMMENTS.LABEL')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.updateUserTrainingData)
        .build()
    );

    fields.push(
      this.formlyService.aGroup()
        .withoutWrapper()
        .withWrapperClass('row col-space-bottom')
        .withValueClass('col-md-7 col-sm-10')
        .withFields([
          this.formlyService.aCheckbox('responsive', 'APP.AUDIT.RESPONSIVE.LABEL')
            .withoutWrapper()
            .withWrapperClass(checkboxClass)
            .onChange(this.updateUserTrainingData)
            .build(),
          this.formlyService.aCheckbox('usingGA', 'APP.AUDIT.USINGGA.LABEL')
            .withoutWrapper()
            .withWrapperClass(checkboxClass)
            .onChange(this.updateUserTrainingData)
            .build(),
          this.formlyService.aCheckbox('canDemoGA', 'APP.AUDIT.CANDEMOGA.LABEL')
            .withoutWrapper()
            .withWrapperClass(checkboxClass)
            .onChange(this.updateUserTrainingData)
            .build()
        ])
        .build()
    );

    fields.push(
      this.formlyService.aValue('trainingModification')
        .withHideExpression(() => !(this.model.training && this.model.training.trainingModification))
        .withoutWrapper()
        .withWrapperClass('row col-space-top')
        .withValueClass('col-sm-12')
        .build()
    );

    return fields;
  }

  private updateUserTrainingData = ():void => {
    this.userProfileService.updateUserTrainingData(this.model.training);
  };

  public ngOnDestroy(): void {
    this.eventHandler.unsubscribe();
  }
}